import { Alerta, Button, Card, DataList, Desplegable, MontoField, Row, Spinner, Stack, TextField } from "@/components";
import { formatMoneda } from "@/lib/format";
import { formatM2, formatPorcentajeUnidad, coeficienteAInput } from "@/lib/unidades";
import { enEstandar, etiquetasSuperficie, filasResumen, MULTIPLICADORES, type CampoMultiplicador, type FilaResumen } from "@/lib/ponderacion";
import { usePonderacionTab } from "./usePonderacionTab";

const m2 = (valor: string | null) => (valor === null ? "—" : formatM2(valor));
// Nombre accesible de cada multiplicador (lo visible es la superficie: "50 m² cub.").
const NOMBRES: Record<CampoMultiplicador, string> = { coef_cubierta: "× cubierta", coef_semicubierta: "× semicub.", coef_descubierta: "× descub." };

const COLUMNAS_RESUMEN = [
  { clave: "nombre", titulo: "Unidad", render: (f: FilaResumen) => (f.fuera_de_venta ? `${f.nombre} (fuera de venta)` : f.nombre) },
  { clave: "propios", titulo: "m² reales", render: (f: FilaResumen) => m2(f.m2Propios) },
  { clave: "descubiertos", titulo: "m² desc. reales", render: (f: FilaResumen) => m2(f.m2Descubiertos) },
  { clave: "ponderados", titulo: "m² ponderados", render: (f: FilaResumen) => m2(f.ponderados) },
  { clave: "ratio", titulo: "Ratio (coef. altura)", render: (f: FilaResumen) => (f.ratio === null ? "—" : coeficienteAInput(f.ratio)) },
  { clave: "homologados", titulo: "m² homologados", render: (f: FilaResumen) => m2(f.homologados) },
  { clave: "porcentaje", titulo: "%", render: (f: FilaResumen) => (f.porcentaje === null ? "—" : formatPorcentajeUnidad(f.porcentaje)) },
  { clave: "valor", titulo: "Valor", render: (f: FilaResumen) => (f.valor === null ? "—" : formatMoneda(f.valor, "USD")) },
];

export function PonderacionTab() {
  const t = usePonderacionTab();
  const d = t.porDefecto;

  return (
    <Stack espacio={6}>
      <Stack espacio={2}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Ponderación</h1>
        <p className="text-chico text-texto-suave">
          Cocheras, bauleras, locales y oficinas entran al % del fideicomiso con su superficie total × un multiplicador (por defecto 1). Los cambios se ven al instante y quedan recién al tocar Guardar: cada paso tiene el suyo, y el precio del resumen también.
        </p>
      </Stack>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}

      {t.form && t.calculo && t.form.departamentos.length === 0 && (
        <Card>
          <p>Todavía no hay departamentos. Cargalos en Unidades y después volvé acá para ponderarlos.</p>
        </Card>
      )}

      {t.form && t.calculo && t.form.departamentos.length > 0 && (
        <Stack espacio={2}>
          <form onSubmit={(e) => void t.paso1.guardar(e)} noValidate>
            <Desplegable key={t.paso1.vuelta} abiertoInicial={!t.paso1.plegado} titulo={`1. m² ponderados · ${m2(t.calculo.totalPonderados)} en total`}>
              <Stack espacio={4}>
                <p className="text-chico text-texto-suave">
                  Cubierta × m1 + semicubierta × m2 + descubierta × m3.
                  {d && ` Por defecto ${coeficienteAInput(d.coef_cubierta)} / ${coeficienteAInput(d.coef_semicubierta)} / ${coeficienteAInput(d.coef_descubierta)} (se cambia en Configuración → Ponderación).`}
                  {" "}Cocheras y demás: su superficie total × un multiplicador. Se guarda aparte, con su botón.
                </p>
                <div>
                  {t.form.departamentos.map((dep) => {
                    const c = t.calculo?.departamentos[dep.id];
                    const err = t.errores.departamentos?.[dep.id] ?? {};
                    const etiquetas = etiquetasSuperficie(dep);
                    return (
                      <div key={dep.id} className="flex items-end justify-between gap-3 border-t border-borde py-2 first:border-t-0 first:pt-0">
                        <div className="w-16 shrink-0 pb-2 leading-tight">
                          <span className="font-medium">{dep.nombre}</span>
                          {dep.fuera_de_venta && <span className="block text-chico text-texto-suave">Fuera de venta</span>}
                        </div>
                        <div className="grid min-w-0 max-w-xs flex-1 grid-cols-3 items-end gap-2">
                          {MULTIPLICADORES.map((campo, i) => (
                            <TextField
                              key={campo}
                              etiqueta={etiquetas[i]!}
                              aria-label={`${NOMBRES[campo]} ${dep.nombre}`}
                              inputMode="decimal"
                              value={dep[campo]}
                              onChange={(e) => t.cambiarDepto(dep.id, campo, e.target.value)}
                              error={err[campo]}
                              className="h-9 px-2"
                              contenedorClassName="min-w-0 gap-1 [&>label]:font-normal [&>label]:leading-tight [&>label]:text-texto-suave"
                            />
                          ))}
                        </div>
                        <span className="shrink-0 pb-2 text-right font-semibold" aria-label={`m² ponderados del ${dep.nombre}`}>
                          {m2(c?.ponderados ?? null)}
                        </span>
                      </div>
                    );
                  })}
                  {t.form.otras.map((o) => (
                    <div key={o.id} className="flex items-end justify-between gap-3 border-t border-borde py-2">
                      <div className="w-16 shrink-0 pb-2 leading-tight">
                        <span className="font-medium">{o.nombre}</span>
                        {o.fuera_de_venta && <span className="block text-chico text-texto-suave">Fuera de venta</span>}
                      </div>
                      <div className="grid min-w-0 max-w-xs flex-1 grid-cols-3 items-end gap-2">
                        <TextField
                          etiqueta={`${formatM2(o.m2)} total`}
                          aria-label={`× superficie ${o.nombre}`}
                          inputMode="decimal"
                          value={o.multiplicador}
                          onChange={(e) => t.cambiarOtra(o.id, e.target.value)}
                          error={t.errores.otras?.[o.id]}
                          className="h-9 px-2"
                          contenedorClassName="min-w-0 gap-1 [&>label]:font-normal [&>label]:leading-tight [&>label]:text-texto-suave"
                        />
                      </div>
                      <span className="shrink-0 pb-2 text-right font-semibold" aria-label={`m² ponderados de ${o.nombre}`}>
                        {m2(t.calculo?.otras[o.id]?.homologados ?? null)}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-right font-semibold">Total: {m2(t.calculo.totalPonderados)} ponderados</p>
                <Alerta texto={t.paso1.error} />
                <Row justificar="fin" espacio={2}>
                  <Button variante="secundario" onClick={t.paso1.descartar} disabled={!t.paso1.cambios || t.paso1.guardando}>
                    Descartar
                  </Button>
                  <Button type="submit" cargando={t.paso1.guardando} disabled={!t.paso1.cambios && t.paso1.plegado}>
                    Guardar m² ponderados
                  </Button>
                </Row>
              </Stack>
            </Desplegable>
          </form>
          {t.paso1.guardado && !t.paso1.cambios && (
            <p role="status" className="rounded-control bg-exito-suave px-3 py-2 text-chico text-exito">
              m² ponderados guardados
            </p>
          )}
        </Stack>
      )}

      {t.form && t.calculo && t.form.departamentos.length > 0 && (
        <Stack espacio={2}>
          <form onSubmit={(e) => void t.paso2.guardar(e)} noValidate>
            <Desplegable key={t.paso2.vuelta} abiertoInicial={!t.paso2.plegado} titulo={`2. Calidad y altura → m² homologados · ${m2(t.calculo.totalHomologados)} en total`}>
              <Stack espacio={4}>
                <p className="text-chico text-texto-suave">
                  m² ponderados × coef. altura de cada depto. Arranca en el estándar de su piso (Configuración → Ponderación); cambialo para un depto en particular (ej. un contrafrente). Se guarda aparte, con su botón.
                </p>
                <div>
                  {t.form.departamentos.map((dep, i, todos) => {
                    const c = t.calculo?.departamentos[dep.id];
                    const nuevoPiso = i === 0 || todos[i - 1]!.piso !== dep.piso;
                    const propio = !enEstandar(dep);
                    return (
                      <div key={dep.id} className={`flex items-center justify-between gap-3 py-1.5 ${nuevoPiso ? "border-t border-borde first:border-t-0" : ""}`}>
                        <div className="w-16 shrink-0 leading-tight">
                          <span className="font-medium">{dep.nombre}</span>
                          {dep.fuera_de_venta && <span className="block text-chico text-texto-suave">Fuera de venta</span>}
                        </div>
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <TextField
                            etiqueta={`Coef. altura ${dep.nombre}`}
                            inputMode="decimal"
                            value={dep.coef_altura}
                            onChange={(e) => t.cambiarDepto(dep.id, "coef_altura", e.target.value)}
                            error={t.errores.departamentos?.[dep.id]?.coef_altura}
                            className="h-9 w-20 px-2"
                            contenedorClassName="gap-0 [&>label]:sr-only"
                          />
                          {propio && (
                            <Button variante="fantasma" tamanio="sm" onClick={() => t.cambiarDepto(dep.id, "coef_altura", dep.estandar)} aria-label={`Usar el estándar del ${dep.nombre}`}>
                              {`Estándar ${dep.estandar}`}
                            </Button>
                          )}
                        </div>
                        <div className="shrink-0 text-right leading-tight">
                          <span className="font-semibold" aria-label={`m² homologados del ${dep.nombre}`}>
                            {m2(c?.homologados ?? null)}
                          </span>
                          <span className="block text-chico text-texto-suave">{dep.fuera_de_venta ? "sin %" : c?.porcentaje ? formatPorcentajeUnidad(c.porcentaje) : "—"}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Stack espacio={1} className="text-right">
                  {t.hayOtras && (
                    <p className="text-chico text-texto-suave">
                      Departamentos {m2(t.calculo.totalHomologadosDeptos)} + cocheras y demás {m2(t.calculo.totalHomologadosOtras)} (sus m² del paso 1, sin coef. altura)
                    </p>
                  )}
                  <p className="font-semibold">Total: {m2(t.calculo.totalHomologados)} homologados = 100 % del fideicomiso</p>
                </Stack>
                <Alerta texto={t.paso2.error} />
                <Row justificar="fin" espacio={2}>
                  <Button variante="secundario" onClick={t.paso2.descartar} disabled={!t.paso2.cambios || t.paso2.guardando}>
                    Descartar
                  </Button>
                  <Button type="submit" cargando={t.paso2.guardando} disabled={!t.paso2.cambios && t.paso2.plegado}>
                    Guardar m² homologados
                  </Button>
                </Row>
              </Stack>
            </Desplegable>
          </form>
          {t.paso2.guardado && !t.paso2.cambios && (
            <p role="status" className="rounded-control bg-exito-suave px-3 py-2 text-chico text-exito">
              m² homologados guardados
            </p>
          )}
        </Stack>
      )}

      {t.form && t.calculo && t.form.departamentos.length > 0 && (
        <form onSubmit={(e) => void t.precio.guardar(e)} noValidate>
          <Card>
            <Stack espacio={4}>
              <Stack espacio={1}>
                <h2 className="text-titulo font-light tracking-tight">Resumen y precio</h2>
                <p className="text-chico text-texto-suave">
                  Probá distintos precios por m² homologado: el valor de cada unidad (redondeado a USD 1.000) y la recaudación se recalculan al instante. Se guarda sólo si tocás Guardar precio; ese es el precio sugerido que se ve en Unidades.
                </p>
              </Stack>
              <div className="max-w-xs">
                <MontoField etiqueta="Precio base por m² homologado" valor={t.form.precio_base} onCambio={t.cambiarPrecioBase} moneda="USD" error={t.errores.precio_base} />
              </div>
              <DataList etiqueta="Resumen de la ponderación" columnas={COLUMNAS_RESUMEN} filas={filasResumen(t.form, t.calculo)} claveFila={(f) => f.id} />
              <Stack espacio={1} className="text-right">
                <p className="text-chico text-texto-suave">
                  {m2(t.calculo.totalHomologados)} homologados{t.form.precio_base.trim() !== "" && t.calculo.recaudacion === null ? " · revisá el precio o los coeficientes" : ""}
                </p>
                <p className="text-subtitulo font-semibold" aria-label="Recaudación total">
                  Recaudación total: {t.calculo.recaudacion === null ? "—" : formatMoneda(t.calculo.recaudacion, "USD")}
                </p>
              </Stack>
              <Alerta texto={t.precio.error} />
              {t.precio.guardado && !t.precio.cambios && (
                <p role="status" className="rounded-control bg-exito-suave px-3 py-2 text-chico text-exito">
                  Precio guardado
                </p>
              )}
              <Row justificar="fin" espacio={2}>
                <Button variante="secundario" onClick={t.precio.descartar} disabled={!t.precio.cambios || t.precio.guardando}>
                  Descartar
                </Button>
                <Button type="submit" cargando={t.precio.guardando} disabled={!t.precio.cambios}>
                  Guardar precio
                </Button>
              </Row>
            </Stack>
          </Card>
        </form>
      )}
    </Stack>
  );
}
